<template>
  <div>
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: '/zhaoshang/customer' }">客户管理</el-breadcrumb-item>
      <el-breadcrumb-item>项目转让</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 三栏布局：左栏（原项目信息+解约合同+受让客户） + 中栏（新合同与新签约款） + 右栏（附加信息） -->
    <el-card class="box-card" style="margin-top: 15px;">
      <div slot="header" class="flexContainer">
        <span class="cardHeader">项目转让</span>
      </div>

      <div v-if="loading" style="text-align:center;padding:40px;">
        <i class="el-icon-loading" style="font-size:24px;color:#409eff;"></i>
        <span style="margin-left:8px;color:#909399;">加载原项目信息...</span>
      </div>

      <el-form v-else :model="form" ref="formRef" :rules="formRules" label-width="110px" size="medium">
        <el-alert
          v-if="isBuildingTransfer"
          type="warning"
          :closable="false"
          show-icon
          style="margin-bottom: 18px;"
          :title="`该门店当前为${oldStore.status || '筹建中'}，转让后保留其余节点进度（当前 ${buildProgressText}）；已完成的客户信息节点将重新打开并清空资料，需督导按受让人重新填写/上传。不生成“转让项目”待办。`"
          :description="reopenItemNames ? `本次需重开：${reopenItemNames}` : '当前没有需要重开的已完成客户信息节点，未完成节点继续在筹建流程办理。'"
        />
        <div class="threeColContainer">

          <!-- ==================== 左栏：原项目只读 + 解约合同 + 受让客户与签约 ==================== -->
          <div class="leftCol">
            <div class="colHeader">原项目信息（转让后终止）</div>

            <el-form-item label="项目名称：">
              <el-input :value="oldProject.projectName" disabled></el-input>
            </el-form-item>

            <el-form-item label="客户：">
              <el-input :value="oldProject.customerName" disabled></el-input>
            </el-form-item>

            <el-form-item label="签约人：">
              <el-input :value="oldProject.signerName" disabled></el-input>
            </el-form-item>

            <el-form-item label="签约时间：">
              <el-input :value="oldProject.dealDate" disabled></el-input>
            </el-form-item>

            <!-- 选择解约合同（解约信息使用默认值，不再单独展示） -->
            <el-form-item label="解约合同：" prop="rescindContractId">
              <el-select v-model="form.rescindContractId" placeholder="请选择要解约的合同" style="width: 100%">
                <el-option
                  v-for="item in oldContracts"
                  :key="item._id"
                  :label="`${item.contractNo}（${item.contractType === 1 ? '新签' : '续签'}）`"
                  :value="item._id">
                </el-option>
              </el-select>
            </el-form-item>

            <!-- 解除协议附件（选填：随老合同解约的退款记录留存） -->
            <el-form-item label="解除协议：">
              <el-upload ref="rescindUpload" class="transferUpload" :action="uploadUrl" :headers="uploadHeaders"
                :on-success="handleRescindUploadSuccess" :on-remove="handleRescindRemove" :on-preview="handlePreview"
                :before-upload="beforeUpload" :file-list="rescindFileList" list-type="picture-card">
                <i class="el-icon-plus"></i>
                <div slot="tip" class="el-upload__tip">选填，上传转让方签署的转让协议/解除协议，留存至解约退款记录</div>
              </el-upload>
            </el-form-item>

            <el-divider></el-divider>

            <!-- 受让客户选择 -->
            <el-form-item label="受让客户：" prop="newCustomerId">
              <el-select
                v-model="form.newCustomerId"
                filterable
                remote
                reserve-keyword
                placeholder="请输入客户名称/手机号搜索"
                :remote-method="searchCustomers"
                :loading="customerLoading"
                style="width: 100%"
                @change="onCustomerChange">
                <el-option
                  v-for="item in customerOptions"
                  :key="item._id"
                  :label="`${item.title}（${item.name} ${item.mobile}）`"
                  :value="item._id">
                </el-option>
              </el-select>
              <div style="margin-top:4px;">
                <el-link type="primary" :underline="false" @click="goCreateCustomer">
                  <i class="el-icon-circle-plus-outline"></i> 客户不存在？先去客户管理创建
                </el-link>
              </div>
            </el-form-item>

            <!-- 项目名称 -->
            <el-form-item label="项目名称：" prop="projectName">
              <el-input v-model="form.projectName" placeholder="默认沿用原项目名，可修改" maxlength="50"></el-input>
            </el-form-item>

            <!-- 签约人（由受让客户创建人自动确定，不可手选） -->
            <el-form-item label="签约人：" prop="signerId">
              <el-input :value="form.signerName" disabled placeholder="选择受让客户后自动确定"></el-input>
              <div style="font-size:12px;color:#999;margin-top:4px;">客户归谁、项目归谁：由受让客户创建人自动确定</div>
            </el-form-item>

            <!-- 签约时间 -->
            <el-form-item label="签约时间：" prop="dealDate">
              <el-date-picker v-model="form.dealDate" type="date" placeholder="请选择签约时间" style="width: 65%;margin-right:5px"
                value-format="yyyy-MM-dd" format="yyyy 年 MM 月 dd 日">
              </el-date-picker>
              <el-button plain type="primary" size="mini" @click="form.dealDate = today">今天</el-button>
            </el-form-item>

            <!-- 转让原因（必填：同时记入操作日志与项目备注） -->
            <el-form-item label="转让原因：" prop="transferReason">
              <el-input v-model="form.transferReason" type="textarea" placeholder="必填，将记入操作日志与项目备注，例如：客户更替经营主体"
                maxlength="200" rows="3" show-word-limit></el-input>
              <div style="font-size:12px;color:#999;margin-top:4px;">
                内容将同时记入操作日志与项目备注，系统会自动追加：由客户{{ oldProject.customerName }}的原项目{{ oldProject.projectName }}转让而来，转让日期{{ today }}
              </div>
            </el-form-item>
          </div>

          <!-- ==================== 中栏：新合同与新签约款 ==================== -->
          <div class="midCol">
            <div class="colHeader">新合同与新签约款</div>

            <!-- 合同金额 -->
            <el-form-item label="合同金额：" prop="contractAmount">
              <el-input-number v-model="form.contractAmount" :min="0" :step="100" placeholder="请输入金额"
                style="width: 160px"></el-input-number>
              <span style="margin-left:10px;color:#909399;">元</span>
            </el-form-item>

            <!-- 抽佣比例 -->
            <el-form-item label="抽佣比例：" prop="commissionRate">
              <el-input-number v-model="form.commissionRate" :min="0" :max="100" :precision="1" :step="0.5"
                placeholder="请输入" style="width: 160px"></el-input-number>
              <span style="margin-left:10px;color:#909399;">%</span>
            </el-form-item>

            <!-- 合同期限 -->
            <el-form-item label="合同期限：" required>
              <div style="display:flex; align-items:center;">
                <el-form-item prop="serviceStartDate" style="margin-bottom:0;">
                  <el-date-picker v-model="form.serviceStartDate" type="date" placeholder="开始日期"
                    value-format="yyyy-MM-dd" format="yyyy 年 MM 月 dd 日" style="width: 100%">
                  </el-date-picker>
                </el-form-item>
                <span style="margin: 0 10px;">至</span>
                <el-form-item prop="serviceEndDate" style="margin-bottom:0;">
                  <el-date-picker v-model="form.serviceEndDate" type="date" placeholder="结束日期"
                    value-format="yyyy-MM-dd" format="yyyy 年 MM 月 dd 日" style="width: 100%">
                  </el-date-picker>
                </el-form-item>
              </div>
            </el-form-item>

            <!-- 合同备注 -->
            <el-form-item label="合同备注：">
              <el-input v-model="form.contractRemark" type="textarea" placeholder="选填，可补充转让相关信息"
                maxlength="500" rows="2"></el-input>
            </el-form-item>

            <!-- 收款金额 -->
            <el-form-item label="收款金额：" prop="totalAmount">
              <el-input-number v-model="form.totalAmount" :min="0" :step="100" placeholder="请输入金额"
                style="width: 160px"></el-input-number>
              <span style="margin-left:10px;color:#909399;">元</span>
            </el-form-item>

            <!-- 钱款类型 -->
            <el-form-item label="钱款类型：" prop="payType">
              <el-radio-group v-model="form.payType" size='mini'>
                <el-radio-button v-for="item in payTypeList" :key="item" :label="item">{{ item }}</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <!-- 收款账户 -->
            <el-form-item label="收款账户：" prop="payAccount">
              <el-select v-model="form.payAccount" placeholder="请选择收款账户" style="width: 100%">
                <el-option v-for="item in payAccountList" :key="item" :label="item" :value="item"></el-option>
              </el-select>
            </el-form-item>

            <!-- 收款日期 -->
            <el-form-item label="收款日期：" prop="payDate">
              <el-date-picker v-model="form.payDate" type="date" placeholder="请选择收款日期" style="width: 65%;margin-right:5px"
                format="yyyy 年 MM 月 dd 日" value-format="yyyy-MM-dd">
              </el-date-picker>
              <el-button plain type="primary" size="mini" @click="form.payDate = today">今天</el-button>
            </el-form-item>

            <!-- 收款备注 -->
            <el-form-item label="收款备注：" prop="payRemark">
              <el-input v-model="form.payRemark" type="textarea" placeholder="请输入备注信息（最多500字符）" maxlength="500"
                rows="2"></el-input>
            </el-form-item>

            <!-- 附件 -->
            <el-form-item label="附件：">
              <el-upload ref="newContractUpload" class="transferUpload" :action="uploadUrl" :headers="uploadHeaders"
                :on-success="handleUploadSuccess" :on-remove="handleRemove" :on-preview="handlePreview"
                :before-upload="beforeUpload" :file-list="newContractFileList" list-type="picture-card">
                <i class="el-icon-plus"></i>
                <div slot="tip" class="el-upload__tip">支持图片、PDF等文件格式</div>
              </el-upload>
            </el-form-item>
          </div>

          <!-- ==================== 右栏：附加信息（8项必填重填，其余沿用原项目）
               首批物料备忘：转让时界面不录入，后端直接复制继承原项目备忘 ==================== -->
          <div class="rightCol">
            <div class="colHeader">附加信息</div>
            <div style="font-size:12px;color:#999;margin-bottom:12px;">以下必填项需重新填写，其余附加信息将自动沿用原项目数据</div>

            <el-form-item size="mini" label="评估单量：" label-width="100px" prop="additionalInfo.评估单量">
              <el-input size='mini' v-model="form.additionalInfo['评估单量']" placeholder="请输入评估单量"></el-input>
            </el-form-item>
            <el-form-item size="mini" label="客户签约代表：" label-width="120px" prop="additionalInfo.客户签约代表">
              <el-input size='mini' v-model="form.additionalInfo['客户签约代表']" placeholder="请输入客户签约代表"></el-input>
            </el-form-item>
            <el-form-item size="mini" label="联系电话：" label-width="100px" prop="additionalInfo.联系电话">
              <el-input size='mini' v-model="form.additionalInfo['联系电话']" placeholder="请输入联系电话"></el-input>
            </el-form-item>
            <el-form-item size="mini" label="房租：" label-width="100px" prop="additionalInfo.房租">
              <el-input size='mini' v-model="form.additionalInfo['房租']" placeholder="请输入房租（填入0表示不确定）"></el-input>
            </el-form-item>
            <el-form-item size="mini" label="运费：" label-width="100px" prop="additionalInfo.运费">
              <el-input size='mini' v-model="form.additionalInfo['运费']" placeholder="请输入运费"></el-input>
            </el-form-item>
            <el-form-item size="mini" label="餐饮经验：" label-width="100px" prop="additionalInfo.餐饮经验">
              <el-input size='mini' v-model="form.additionalInfo['餐饮经验']" placeholder="请输入餐饮经验"></el-input>
            </el-form-item>
            <el-form-item size="mini" label="是否翻新：" label-width="100px" prop="additionalInfo.是否翻新">
              <el-input size='mini' v-model="form.additionalInfo['是否翻新']" placeholder="请输入是否翻新"></el-input>
            </el-form-item>
            <el-form-item size="mini" label="情况说明：" label-width="100px" prop="additionalInfo.情况说明">
              <el-input size='mini' type="textarea" :rows="4" v-model="form.additionalInfo['情况说明']" placeholder="请输入情况说明"></el-input>
            </el-form-item>
          </div>
        </div>

        <!-- 提交区 -->
        <el-divider></el-divider>
        <el-form-item label-width="0">
          <div style="text-align:left;">
            <el-button type="primary" :loading="submitting" @click="okClickHandler" icon="el-icon-refresh-right">
              确认转让
            </el-button>
            <el-button @click="goBack">取消</el-button>
          </div>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- PDF预览对话框 -->
    <el-dialog :visible.sync="pdfPreviewDialogVisible" width="60%" :title="previewTitle">
      <div class="preview-container" style="height: 600px;">
        <iframe :src="previewUrl" style="width: 100%; height: 100%; border: none;"></iframe>
      </div>
    </el-dialog>

    <!-- 图片预览 -->
    <el-image v-show="false" ref="imageViewer" :src="currentImageUrl" :preview-src-list="imagePreviewList">
    </el-image>
  </div>
</template>

<script>
import { additionalInfo, payAccountList, payTypeListForNew } from '@/utility/staticData'

export default {
  name: 'ProjectTransfer',
  data () {
    return {
      today: this.formatDate(new Date()),
      loading: true,
      submitting: false,
      customerLoading: false,
      uploadUrl: this.$http.defaults.baseURL + 'upload',
      uploadHeaders: { authorization: window.sessionStorage.getItem('token'), Accept: 'application/json' },

      // 老项目信息（从接口加载）
      oldProject: {
        projectName: '',
        customerId: '',
        customerName: '',
        signType: '',
        signerName: '',
        dealDate: ''
      },

      // 客户下拉选项
      customerOptions: [],

      // 老项目生效中合同列表
      oldContracts: [],

      // 转让模式与筹建信息
      transferMode: 'operating',
      oldStore: { status: '' },
      buildFlow: null,

      payTypeList: payTypeListForNew,
      payAccountList: payAccountList,

      // 上传展示列表（存 el-upload 内部文件对象，缩略图用其 blob/图标 url；提交数据在 form.attachments/rescindAttachments 存相对路径）
      newContractFileList: [],
      rescindFileList: [],

      // 预览相关
      pdfPreviewDialogVisible: false,
      previewUrl: '',
      previewTitle: '',
      currentImageUrl: '',
      imagePreviewList: [],

      // PDF 图标路径
      pdfIconUrl: require('@/assets/pdf.png'),

      // 用于预览的基础url
      base: '',

      // 表单
      form: {
        newCustomerId: '',
        projectName: '',
        signerId: '',
        signerName: '',
        dealDate: '',
        contractAmount: 0,
        commissionRate: 5,
        serviceStartDate: '',
        serviceEndDate: '',
        contractRemark: '',
        totalAmount: 0,
        payType: '全款',
        payAccount: '',
        payDate: '',
        payRemark: '',
        transferReason: '',
        attachments: [],
        // 附加信息（加载后预填老项目数据）
        additionalInfo: Object.assign({ 房租: '', 商圈已测: '' }, additionalInfo()),
        // 老合同解约（解约信息使用默认值；解除协议附件在页面选填上传）
        rescindContractId: '',
        rescindFee: 0,
        refundAmount: 0,
        refundAccount: '',
        refundDate: '',
        rescindAttachments: []
      },

      formRules: {
        transferReason: [{ required: true, message: '请填写转让原因（将记入操作日志）', trigger: 'blur' }],
        newCustomerId: [{ required: true, message: '请选择受让客户', trigger: 'change' }],
        projectName: [{ required: true, message: '请输入项目名称', trigger: 'blur' }],
        signerId: [{ required: true, message: '请先选择受让客户，签约人将自动确定', trigger: 'change' }],
        dealDate: [{ required: true, message: '请选择签约时间', trigger: 'change' }],
        contractAmount: [{ required: true, message: '请输入合同金额', trigger: 'blur' }],
        commissionRate: [{ required: true, message: '请输入抽佣比例', trigger: 'blur' }],
        serviceStartDate: [{ required: true, message: '请选择合同开始日期', trigger: 'change' }],
        serviceEndDate: [{ required: true, message: '请选择合同结束日期', trigger: 'change' }],
        totalAmount: [{ required: true, message: '请输入收款金额', trigger: 'blur' }],
        payType: [{ required: true, message: '请选择钱款类型', trigger: 'change' }],
        payAccount: [{ required: true, message: '请选择收款账户', trigger: 'change' }],
        payDate: [{ required: true, message: '请选择收款日期', trigger: 'change' }],
        rescindContractId: [{ required: true, message: '请选择要解约的合同', trigger: 'change' }],
        additionalInfo: {
          评估单量: [{ required: true, message: '请输入评估单量', trigger: 'blur' }],
          客户签约代表: [{ required: true, message: '请输入客户签约代表', trigger: 'blur' }],
          联系电话: [{ required: true, message: '请输入联系电话', trigger: 'blur' }],
          房租: [{ required: true, message: '请输入房租', trigger: 'blur' }],
          运费: [{ required: true, message: '请输入运费', trigger: 'blur' }],
          餐饮经验: [{ required: true, message: '请输入餐饮经验', trigger: 'blur' }],
          是否翻新: [{ required: true, message: '请输入是否翻新', trigger: 'blur' }],
          情况说明: [{ required: true, message: '请输入情况说明', trigger: 'blur' }]
        }
      }
    }
  },
  computed: {
    oldProjectId () {
      return this.$route.query.oldProjectId || ''
    },
    isBuildingTransfer () {
      return this.transferMode === 'building'
    },
    buildProgressText () {
      if (!this.buildFlow || !this.buildFlow.progress) return ''
      const p = this.buildFlow.progress
      return `${p.completed || 0}/${p.total || 0}`
    },
    reopenItemNames () {
      if (!this.buildFlow || !Array.isArray(this.buildFlow.reopenItems)) return ''
      return this.buildFlow.reopenItems.map(item => item.name).join('、')
    }
  },
  methods: {
    formatDate (date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },

    // 加载老项目信息和生效中合同（专用接口，一次返回）
    async loadOldProject () {
      this.loading = true
      try {
        const { data: res } = await this.$http.get(`zhaoshang/project/transferInit/${this.oldProjectId}`)
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          this.goBack()
          return
        }
        if (res.data.transferMode === 'unavailable') {
          this.$message.error('当前项目或门店状态不可转让')
          this.goBack()
          return
        }
        if (res.data.supervisorInactive) {
          this.$message.error('老项目督导已离职，请先更换督导后再转让')
          this.goBack()
          return
        }
        // 双保险：无筹建督导不允许转让（与转让按钮预检同源，防直接 URL 进入）
        if (!res.data.constructionSupervisor) {
          this.$message.error('老项目无有效筹建督导，无法转让，请先为项目门店分配筹建督导')
          this.goBack()
          return
        }
        if (res.data.transferMode === 'building' && !res.data.buildFlow) {
          const msg = res.data.store && res.data.store.status === '待运营'
            ? '筹建流程已完成，请走运营转让或联系管理员处理'
            : '老项目无进行中的筹建流程，无法转让'
          this.$message.error(msg)
          this.goBack()
          return
        }
        this.transferMode = res.data.transferMode || 'operating'
        this.oldStore = res.data.store || { status: '' }
        this.buildFlow = res.data.buildFlow || null
        const contracts = res.data.contracts || []
        if (this.transferMode === 'building' && contracts.length !== 1) {
          this.$message.error('筹建中/待运营转让要求仅一份生效合同，请先处理至一份')
          this.goBack()
          return
        }
        const p = res.data.project
        this.oldProject = {
          projectName: p.projectName,
          customerId: p.customerId || '',
          customerName: p.customerName,
          signType: p.signType,
          signerName: p.signerName,
          dealDate: p.dealDate || ''
        }
        // 默认项目名沿用
        this.form.projectName = p.projectName
        // 默认合同备注
        this.form.contractRemark = `接手客户${p.customerName}的${p.projectName}转让，新签合同`
        // 默认收款备注
        this.form.payRemark = `接手客户${p.customerName}的${p.projectName}转让`
        // 预填附加信息：全量沿用老项目（含历史额外键），仅以下8项必填留空由用户重新填写
        const mergedAdditionalInfo = Object.assign({ 房租: '', 商圈已测: '' }, additionalInfo(), p.additionalInfo || {})
        ;['评估单量', '客户签约代表', '联系电话', '房租', '运费', '餐饮经验', '是否翻新', '情况说明'].forEach(key => {
          mergedAdditionalInfo[key] = ''
        })
        this.form.additionalInfo = mergedAdditionalInfo
        // 生效中合同列表
        this.oldContracts = contracts
        if (contracts.length === 1) {
          this.form.rescindContractId = contracts[0]._id
        }
      } catch (error) {
        this.$message.error('加载项目信息失败')
        this.goBack()
      } finally {
        this.loading = false
      }
    },

    // 搜索受让客户（转让专用接口：不做"仅自己"数据隔离，可搜到其他专员名下客户；排除原客户；
    // 候选已过滤创建人非在职的客户）
    async searchCustomers (query) {
      if (!query) {
        this.customerOptions = []
        return
      }
      this.customerLoading = true
      try {
        const { data: res } = await this.$http.get('zhaoshang/project/transferCustomers', {
          params: { query: query, excludeCustomerId: this.oldProject.customerId || undefined }
        })
        if (res.meta.status === 200) {
          this.customerOptions = res.data.list || []
        }
      } catch (error) {
        // 静默
      } finally {
        this.customerLoading = false
      }
    },

    // 受让客户选定后，签约人自动确定为其创建人（后端强校验一致，不可手选）
    onCustomerChange (id) {
      const customer = this.customerOptions.find(c => c._id === id)
      this.form.signerId = customer ? (customer.creatorId || '') : ''
      this.form.signerName = customer ? (customer.creatorName || '') : ''
    },

    goCreateCustomer () {
      this.$router.push('/zhaoshang/customer/add')
    },

    // 判断文件类型
    getFileType (fileName) {
      const ext = fileName.toLowerCase().split('.').pop()
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'image'
      if (ext === 'pdf') return 'pdf'
      return 'other'
    },

    // 上传相关（与 ProjectAdd 同款架构：展示列表存 el-upload 内部对象，提交数据与展示分离）
    handleUploadSuccess (res, file, fileList) {
      if (!res.meta || res.meta.status !== 200) {
        // HTTP 200 但业务失败（如 token 过期）：el-upload 仍显示成功，需手动移除并提示
        this.$message.error((res.meta && res.meta.msg) || '附件上传失败，请重新上传')
        this.$refs.newContractUpload.handleRemove(file)
        return
      }
      const fileType = this.getFileType(file.name)
      if (fileType === 'pdf') {
        const fileItem = fileList[fileList.length - 1]
        fileItem.url = this.pdfIconUrl // 缩略图显示PDF图标
        fileItem.actualUrl = this.base + res.data.url // 实际文件地址用于预览
      }
      this.form.attachments.push({ name: file.name, url: res.data.url, type: fileType })
      this.newContractFileList = fileList
    },
    handleRemove (file, fileList) {
      // 展示列表与提交数组按下标一一对应，同步删除
      const index = this.newContractFileList.indexOf(file)
      if (index !== -1 && this.form.attachments[index]) {
        this.form.attachments.splice(index, 1)
      }
      this.newContractFileList = fileList
    },
    // 解除协议附件（随解约退款记录留存）
    handleRescindUploadSuccess (res, file, fileList) {
      if (!res.meta || res.meta.status !== 200) {
        this.$message.error((res.meta && res.meta.msg) || '解除协议上传失败，请重新上传')
        this.$refs.rescindUpload.handleRemove(file)
        return
      }
      const fileType = this.getFileType(file.name)
      if (fileType === 'pdf') {
        const fileItem = fileList[fileList.length - 1]
        fileItem.url = this.pdfIconUrl
        fileItem.actualUrl = this.base + res.data.url
      }
      this.form.rescindAttachments.push({ name: file.name, url: res.data.url, type: fileType })
      this.rescindFileList = fileList
    },
    handleRescindRemove (file, fileList) {
      const index = this.rescindFileList.indexOf(file)
      if (index !== -1 && this.form.rescindAttachments[index]) {
        this.form.rescindAttachments.splice(index, 1)
      }
      this.rescindFileList = fileList
    },
    // 预览（图片用 el-image 查看器，PDF 用 iframe 弹窗，其他类型新标签页打开）
    handlePreview (file) {
      const attachment = this.form.attachments.find(item => item.name === file.name) ||
        this.form.rescindAttachments.find(item => item.name === file.name)
      if (!attachment) return
      const fileUrl = this.base + attachment.url
      this.previewTitle = file.name
      if (attachment.type === 'image') {
        const source = this.form.attachments.includes(attachment)
          ? this.form.attachments
          : this.form.rescindAttachments
        this.imagePreviewList = source.filter(item => item.type === 'image').map(item => this.base + item.url)
        this.currentImageUrl = fileUrl
        this.$nextTick(() => {
          this.$refs.imageViewer.clickHandler()
        })
      } else if (attachment.type === 'pdf') {
        this.previewUrl = file.actualUrl || fileUrl
        this.pdfPreviewDialogVisible = true
      } else {
        window.open(fileUrl)
      }
    },
    beforeUpload (file) {
      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        this.$message.warning('文件大小不能超过10MB')
        return false
      }
      return true
    },

    // 提交转让
    okClickHandler () {
      this.$refs.formRef.validate(async valid => {
        if (!valid) {
          this.$message.warning('请填写完整必填项')
          return
        }

        // 附件上传中禁止提交，避免文件静默漏传
        const uploadingCount = [this.$refs.newContractUpload, this.$refs.rescindUpload]
          .filter(ref => ref && ref.uploadFiles)
          .reduce((sum, ref) => sum + ref.uploadFiles.filter(f => f.status === 'uploading').length, 0)
        if (uploadingCount > 0) {
          this.$message.warning('附件正在上传中，请等待上传完成后再提交')
          return
        }

        try {
          const confirmText = this.isBuildingTransfer
            ? '确认转让？转让后原项目将终止，新项目继续筹建；已完成的客户信息节点会重新打开并清空原资料，需督导按受让人重新填写/上传。此操作不可逆。'
            : '确认转让？转让后原项目将解约终止，此操作不可逆。'
          await this.$confirm(confirmText, '提示', {
            type: 'warning'
          })
        } catch {
          return
        }

        this.submitting = true
        try {
          const { data: res } = await this.$http.post(
            `zhaoshang/project/transfer/${this.oldProjectId}`,
            {
              newCustomerId: this.form.newCustomerId,
              projectName: this.form.projectName,
              signerId: this.form.signerId,
              signerName: this.form.signerName,
              dealDate: this.form.dealDate,
              contractAmount: this.form.contractAmount,
              commissionRate: this.form.commissionRate,
              serviceStartDate: this.form.serviceStartDate,
              serviceEndDate: this.form.serviceEndDate,
              contractRemark: this.form.contractRemark,
              totalAmount: this.form.totalAmount,
              payType: this.form.payType,
              payAccount: this.form.payAccount,
              payDate: this.form.payDate,
              payRemark: this.form.payRemark,
              // 转让原因同时作为项目备注（后端会自动追加转让来源信息）
              remark: (this.form.transferReason || '').trim(),
              reason: (this.form.transferReason || '').trim(),
              attachments: this.form.attachments,
              // 附加信息
              additionalInfo: this.form.additionalInfo,
              // 老合同解约（rescindReason/refundRemark 由后端兜底，不传）
              rescindContractId: this.form.rescindContractId,
              rescindFee: this.form.rescindFee,
              refundAmount: this.form.refundAmount,
              refundAccount: this.form.refundAccount,
              refundDate: this.form.refundDate,
              rescindAttachments: this.form.rescindAttachments
            }
          )

          if (res.meta.status !== 200) {
            this.$message.error(res.meta.msg)
            return
          }

          if (res.data.transferFlowError) {
            // operating 转让成功但流程实例创建失败，给用户警告提示
            this.$message.warning(`项目转让成功，但${res.data.transferFlowError}`)
          } else if (res.data.transferMode === 'building') {
            const progress = res.data.buildProgressAfterReopen
              ? `${res.data.buildProgressAfterReopen.completed || 0}/${res.data.buildProgressAfterReopen.total || 0}`
              : ''
            const reopenNames = (res.data.buildReopenItems || []).map(item => item.name).join('、')
            this.$message.success(`项目转让成功，筹建流程已继承${progress ? `（${progress}）` : ''}${reopenNames ? `，重开节点：${reopenNames}` : ''}；请通知筹建督导按受让人更新资料`)
          } else {
            this.$message.success('项目转让成功')
          }
          // 返回客户管理
          this.$router.push('/zhaoshang/customer?tabs=project')
        } catch (error) {
          this.$message.error('转让请求失败')
        } finally {
          this.submitting = false
        }
      })
    },

    goBack () {
      this.$router.back()
    }
  },
  async created () {
    this.base = window.sessionStorage.getItem('base')
    if (!this.oldProjectId) {
      this.$message.error('缺少项目ID')
      this.goBack()
      return
    }
    // 默认签约时间/收款日期为今天
    this.form.dealDate = this.today
    this.form.payDate = this.today
    // 老合同解约默认值（页面不再展示）：退款账户/退款日期
    this.form.refundAccount = '飞赢品牌企业微信'
    this.form.refundDate = this.today

    await this.loadOldProject()
  }
}
</script>

<style lang="less" scoped>
.flexContainer {
  display: flex;
  align-items: center;
}

.cardHeader {
  font-size: 16px;
  font-weight: bold;
}

// 三栏布局容器
.threeColContainer {
  display: flex;
  gap: 25px;
  align-items: flex-start;
}

// 左栏：原项目只读 + 解约合同 + 受让客户与签约
.leftCol {
  width: 400px;
  flex-shrink: 0;
  background-color: #f9fafc;
  padding: 15px;
  border-radius: 4px;
}

// 中栏：新合同与新签约款
.midCol {
  width: 500px;
  flex-shrink: 0;
}

// 右栏：附加信息（灰底与左栏对称）
.rightCol {
  width: 400px;
  flex-shrink: 0;
  background-color: #f9fafc;
  padding: 15px;
  border-radius: 4px;
}

// 每栏的标题
.colHeader {
  font-size: 14px;
  font-weight: bold;
  color: #606266;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

// 左栏只读输入框文字颜色加深
.leftCol {
  ::v-deep .el-input.is-disabled .el-input__inner {
    color: #606266;
    background-color: transparent;
    cursor: default;
  }
}

// PDF预览弹窗内容与图片查看器层级（与 ProjectAdd 一致）
.preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .el-image-viewer__wrapper {
  z-index: 3000;
}
</style>

<style>
.transferUpload .el-upload--picture-card {
  width: 80px;
  height: 80px;
  line-height: 80px;
}

.transferUpload .el-upload-list__item {
  width: 80px !important;
  height: 80px !important;
}
</style>
